import apiFetch from '@scale/frontend-lib/api-fetch';
import { getPageLoadSingleton } from '@scale/frontend-lib/globalSingletons';
import logger from '@scale/frontend-lib/logging';
import { QualificationType } from '@scale/shared-types';
import { BYPASSED_OCP_WHITELIST_SLUG } from '@scale/shared/onboardingControlPanel/constants';
import { userCanAccessOutlier } from '@scale/shared/outlier/login';
import {
  QualificationEntryStatus,
  QualificationJSON,
  QualificationListInfo,
  QualificationListStatus,
} from '@scale/shared/types/qualification/types';
import { IUserJson } from '@scale/shared/types/user';
import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useUser } from 'react-scale/components/ui/UserContext';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { DashboardTodoCards } from 'remotasks/components/Expert/constants';
import { DashboardTodoContent } from 'remotasks/components/Expert/types';

export const normalizePathname = (p: string) =>
  p.endsWith('/') ? p.substring(0, p.length - 1) : p;

export const useDashboardTodos = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  user: IUserJson,
) => {
  const { data } = useQuery('optionalTodos', async () => {
    const data = await apiFetch(null, 'internal/experts/dashboard/optional-todos');
    return data;
  });

  const showDiscourse = useUnleashFeatureFlag('outlier-discourse', {}, false) ?? false;

  const dashboardTodoContent: { [K in DashboardTodoCards]?: DashboardTodoContent } = {
    [DashboardTodoCards.CompletedSecurityTraining]: {
      completed: data?.completedSecurityTraining ?? false,
      exists: !!data,
    },
    [DashboardTodoCards.SetupBanking]: {
      completed: data?.setupBanking ?? false,
      exists: !!data,
    },
    [DashboardTodoCards.CompleteReferral]: {
      completed: data?.isReferralPaid ?? false,
      exists: data?.isReferral ?? false,
    },
    [DashboardTodoCards.JoinedDiscourse]: {
      completed:
        (!!user.scaleDiscourseId && user.scaleDiscourseId !== BYPASSED_OCP_WHITELIST_SLUG) ?? false,
      exists: showDiscourse && !!data,
      required: false,
    },
  };

  const incompleteTodos = Object.keys(dashboardTodoContent)
    .filter(todo => {
      const todoType = todo as DashboardTodoCards;
      const todoContent = dashboardTodoContent[todoType];
      if (!todoContent) return false;
      const { exists, completed } = todoContent;
      return exists && !completed;
    })
    .map(todo => todo as DashboardTodoCards)
    .map(todo => ({
      id: todo,
      required: dashboardTodoContent[todo]?.required,
    }));
  const completedTodos = Object.keys(dashboardTodoContent)
    .filter(todo => {
      const todoType = todo as DashboardTodoCards;
      const todoContent = dashboardTodoContent[todoType];
      if (!todoContent) return false;
      const { exists, completed } = todoContent;
      return exists && completed;
    })
    .map(todo => todo as DashboardTodoCards)
    .map(todo => ({
      id: todo,
      required: dashboardTodoContent[todo]?.required,
    }));
  return { incompleteTodos, completedTodos };
};

export const userCanAccessExpertDashboard = (user?: IUserJson): boolean => {
  if (!user) {
    return false;
  }

  return userCanAccessOutlier(user);
};

export const isOnboardingWorkerTeam = (user?: IUserJson): boolean => {
  return [
    '64b1d99b5644f63f9154dc92',
    '6463b329cce10044305f2ec9',
    '64b73a30d4a9ef6d790515ef',
    '6441d34f09a8a1eb7545f374',
  ].includes(user?.activeWorkerTeam || '');
};

export const isRequiredFirstQualification = (qualification: any): boolean => {
  return ['64c959903233efe14d47a913'].includes(qualification?.id);
};

export const isQualifiedWorker = (
  qualificationsListInfo: QualificationListInfo | undefined,
): boolean => qualificationsListInfo?.qualificationListStatus !== QualificationListStatus.Failed;

export const isQualifiedWorkerByWorkerSkill = (qualificationsListInfo: any): boolean => {
  const requiredQualificationListInfo: QualificationJSON[] =
    qualificationsListInfo?.requiredQualificationListInfo;
  if (!requiredQualificationListInfo) return true;

  const failed = _.some(
    requiredQualificationListInfo.map(
      r =>
        r.qualificationType === QualificationType.WorkerSkill &&
        r.qualificationStatus === QualificationEntryStatus.Failed,
    ),
  );
  return !failed;
};

type WorkerQualificationFailureStatus =
  | {
      failed: false;
    }
  | {
      failed: true;
      failingQualifications: QualificationJSON[];
    };

const getQlistFailureInfo = (
  qlistInfo: QualificationListInfo,
): WorkerQualificationFailureStatus => {
  if (qlistInfo.qualificationListStatus !== QualificationListStatus.Failed) {
    return { failed: false };
  } else {
    return {
      failed: true,
      failingQualifications: qlistInfo.requiredQualificationListInfo.filter(
        qi => qi.qualificationStatus === QualificationEntryStatus.Failed,
      ),
    };
  }
};

type WorkerQualificationStatuses = {
  nonFailingTaskers: any[];
  failingTaskersInfo: { tasker: any; failingQualifications: QualificationJSON[] }[];
};

export const computeTaskerQualificationInfo = (
  qualificationsListInfos: (QualificationListInfo | undefined)[],
  selectedTaskers: $FIXME[],
): WorkerQualificationStatuses => {
  const nonFailingTaskers: $FIXME[] = [];
  const failingTaskersInfo: { tasker: $FIXME; failingQualifications: QualificationJSON[] }[] = [];
  for (const [qlistInfo, tasker] of _.zip(qualificationsListInfos, selectedTaskers)) {
    if (!qlistInfo) {
      // If we could not successfully retrieve the qlistInfo, default to accepting the tasker.
      // In this sense, checking qlist failures is 'best effort'.
      nonFailingTaskers.push(tasker);
    } else {
      const failureInfo = getQlistFailureInfo(qlistInfo);
      if (failureInfo.failed) {
        failingTaskersInfo.push({
          tasker,
          failingQualifications: failureInfo.failingQualifications,
        });
      } else {
        nonFailingTaskers.push(tasker);
      }
    }
  }

  return { nonFailingTaskers, failingTaskersInfo };
};

export const getPrettyEstimatedTime = (minutes?: number) => {
  if (!minutes || minutes === 0) return '';
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  const formatHours = hours > 0 ? `${hours}h` : '';
  const formatMinutes = remainingMinutes > 0 ? `${remainingMinutes}min` : '';

  return formatMinutes ? `${formatHours} ${formatMinutes}` : `${formatHours}`;
};

export const useLogger = () => {
  const user = useUser();

  const loggerContext = useMemo(
    () => ({
      pageLoadId: getPageLoadSingleton(),
      userId: user?.id,
      workerTeam: user?.activeWorkerTeam,
      isExperimental: user?.useExperimentalBranch,
    }),
    [user],
  );

  const info = useCallback(
    (message: string, additionalContext: { [key: string]: any } = {}) => {
      logger.info(message, { ...loggerContext, ...additionalContext });
    },
    [loggerContext],
  );

  const warn = useCallback(
    (message: string, additionalContext: { [key: string]: any } = {}) => {
      logger.warn(message, { ...loggerContext, ...additionalContext });
    },
    [loggerContext],
  );

  const error = useCallback(
    (message: string, additionalContext: { [key: string]: any } = {}, error?: Error) => {
      // @ts-ignore Datadog docs mention 3 args for .error
      logger.error(message, { ...loggerContext, ...additionalContext }, error);
    },
    [loggerContext],
  );

  return {
    info,
    warn,
    error,
  };
};
